import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

/**
 * Maestro-owned customer account drawer. Shopify remains responsible for
 * passwordless authentication; this component owns the storefront entry UI.
 */
class MaestroAccountDrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-maestro-account-trigger]');
    this.dialog = this.querySelector('[data-maestro-account-dialog]');
    this.closeButton = this.querySelector('[data-maestro-account-close]');
    this.storageKey = this.dataset.maestroAccountStorageKey || '';
    this.liquidAuthenticated =
      this.dataset.maestroAccountLiquidAuthenticated === 'true';
    this.authenticatedElements = this.querySelectorAll(
      '[data-maestro-account-authenticated]',
    );
    this.anonymousElements = this.querySelectorAll(
      '[data-maestro-account-anonymous]',
    );
    this.memberName = this.querySelector('[data-maestro-account-member-name]');
    this.memberEmail = this.querySelector('[data-maestro-account-member-email]');
    this.accountText = this.querySelector('.account-button__text');

    this.trigger?.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);
    this.dialog?.addEventListener('click', this.handleBackdropClick);
    this.dialog?.addEventListener('close', this.handleClose);
    window.addEventListener(
      'maestro:customer-session',
      this.handleCustomerSession,
    );
    window.addEventListener('pageshow', this.syncFromStorage);
    window.addEventListener('focus', this.syncFromStorage);
    window.addEventListener('storage', this.handleStorageEvent);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.syncFromStorage();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.open);
    this.closeButton?.removeEventListener('click', this.close);
    this.dialog?.removeEventListener('click', this.handleBackdropClick);
    this.dialog?.removeEventListener('close', this.handleClose);
    window.removeEventListener(
      'maestro:customer-session',
      this.handleCustomerSession,
    );
    window.removeEventListener('pageshow', this.syncFromStorage);
    window.removeEventListener('focus', this.syncFromStorage);
    window.removeEventListener('storage', this.handleStorageEvent);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
  }

  readCustomerSnapshot = () => {
    try {
      const shopSnapshot = this.storageKey
        ? sessionStorage.getItem(`${this.storageKey}:customer`)
        : null;
      return JSON.parse(
        shopSnapshot ||
          sessionStorage.getItem('maestro-account:current-customer') ||
          localStorage.getItem('maestro-account:current-customer') ||
          'null',
      );
    } catch {
      return null;
    }
  };

  hasUsableStoredSession = () => {
    if (!this.storageKey) return false;
    try {
      const session = JSON.parse(
        sessionStorage.getItem(this.storageKey) || 'null',
      );
      return Boolean(
        session?.access_token &&
          Number(session.expiresAt || 0) > Date.now(),
      );
    } catch {
      return false;
    }
  };

  renderCustomerSession = (authenticated, customer = null) => {
    this.authenticatedElements.forEach((element) => {
      element.hidden = !authenticated;
    });
    this.anonymousElements.forEach((element) => {
      element.hidden = authenticated;
    });

    const firstName = String(customer?.firstName || '').trim();
    const email = String(customer?.email || '').trim();
    const liquidFirstName = String(this.memberName?.textContent || '').trim();
    const displayName = firstName || liquidFirstName || 'Maestro member';

    if (this.memberName) this.memberName.textContent = displayName;
    if (this.memberEmail) {
      this.memberEmail.textContent = email;
      this.memberEmail.hidden = !email;
    }
    if (this.trigger) {
      this.trigger.setAttribute(
        'aria-label',
        authenticated
          ? 'Open Maestro account'
          : 'Sign in to your Maestro account',
      );
    }
    if (this.accountText) {
      this.accountText.textContent = authenticated ? 'Account' : 'Sign in';
    }
    if (this.dialog) {
      const suffix = authenticated ? '-title' : '-title-anonymous';
      this.dialog.setAttribute('aria-labelledby', `${this.dialog.id}${suffix}`);
    }
  };

  syncFromStorage = () => {
    const snapshot = this.readCustomerSnapshot();
    if (snapshot && typeof snapshot.authenticated === 'boolean') {
      const snapshotIsCurrent =
        Number(snapshot.expiresAt || 0) > Date.now() ||
        Number(snapshot.resumeUntil || 0) > Date.now() ||
        this.hasUsableStoredSession();
      const authenticated =
        snapshot.authenticated === true && snapshotIsCurrent;
      this.renderCustomerSession(authenticated, authenticated ? snapshot : null);
      return;
    }
    this.renderCustomerSession(this.liquidAuthenticated);
  };

  handleCustomerSession = (event) => {
    // The portal only emits an authenticated event after its Customer Account
    // API request succeeds, so that validated result is authoritative for the
    // drawer. Explicit sign-out and expiry emit an unauthenticated event.
    const authenticated = event.detail?.authenticated === true;
    this.renderCustomerSession(
      authenticated,
      authenticated ? event.detail?.customer : null,
    );
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.syncFromStorage();
  };

  handleStorageEvent = (event) => {
    if (event.key !== 'maestro-account:current-customer') return;
    let snapshot = null;
    try {
      snapshot = JSON.parse(event.newValue || 'null');
    } catch {
      snapshot = null;
    }
    if (snapshot?.authenticated !== true) {
      if (this.storageKey) {
        sessionStorage.removeItem(this.storageKey);
        sessionStorage.removeItem(`${this.storageKey}:customer`);
      }
      sessionStorage.removeItem('maestro-account:current-customer');
    }
    this.syncFromStorage();
  };

  open = () => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    this.dialog.showModal();
    this.trigger?.setAttribute('aria-expanded', 'true');
  };

  close = () => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    this.dialog.close();
  };

  handleBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  handleClose = () => {
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.trigger?.focus();
  };
}

if (!customElements.get('maestro-account-drawer')) {
  customElements.define('maestro-account-drawer', MaestroAccountDrawer);
}
